// TODO: (vivian) now handle by kc, remove after release
/* eslint-disable @typescript-eslint/naming-convention */
import { CircularProgress } from '@ouster/webviz-react-components';

import { isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { RxArrowRight } from 'react-icons/rx';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import AuthForm, { AuthMode } from '../../components/auth/AuthForm';
import EulaViewer from '../../components/auth/EulaViewer';
import Stepper from '../../components/auth/Stepper';
import UserLocation from '../../components/auth/UserLocation';
import sideImg from '../../images/success-image.jpg';
import { useKeycloak } from '../../KeycloakContext';
import { NavBackButton } from '../../layouts/AuthLayout';
import { useStoreState, useStoreActions } from '../../reducers';
import styles from '../PublicPage.module.css';

const InvalidInvitationLink = () => (
  <div className={styles['auth-content']}>
    {/* text copy on left */}
    <div
      className={`${styles['auth-copy-container']} ${styles['auth-register-error-container']}`}
    >
      <div>
        <h2 className={styles['auth-page-title']}>
          Oops! Something went wrong!
        </h2>
        <p
          className={styles['auth-page-copy']}
          style={{ textTransform: 'none' }}
        >
          We had an issue when processing your registration. Please ensure
          you’ve accessed this page through the invitation link provided in your
          email
        </p>
      </div>

      <div className={styles['auth-register-error-button-container']}>
        <Link to="/sign_up" className={styles['primary-button']}>
          Sign up for free <RxArrowRight />
        </Link>
        <Link to="/login" className={styles['secondary-button']}>
          Log in
        </Link>
      </div>
    </div>

    {/* auth form on right */}
    <div className={styles['auth-form-container']}>
      <img src={sideImg} alt="" />
    </div>
  </div>
);

export default function RegisterPage() {
  const { keycloak } = useKeycloak();
  const { setIsAuthenticated, eulaAcceptance, setUser } = useStoreActions(
    (actions) => actions.user
  );
  const { addInvitedUser } = useStoreActions((actions) => actions.organization);
  const { isAuthenticated, user } = useStoreState((storeState) => ({
    ...storeState.user,
  }));
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const organization_uuid = queryParams.get('organization_id');
  const invitation_id = queryParams.get('invitation_id');
  const isOrgActive = queryParams.get('state') === '1';

  const [errorMsg, setErrorMsg] = useState('');
  const [isEulaVisible, setIsEulaVisible] = useState(false);
  const [isEulaAccepted, setIsEulaAccepted] = useState(false);
  const [pendingAuthMethod, setPendingAuthMethod] = useState<string | null>(
    null
  );
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);

  /* 
    if the invitation belongs to active org, it means the user is invited,
    otherwise, the user is self-sign-up.
    when user is invited, they need to sign up first.
  */
  const isSignUpRequired = !!isOrgActive;
  /* 
    When sign up is required, it means user need to provide location information as well
  */
  const [isLocationCollected, setIsLocationCollected] = useState(
    !isSignUpRequired
  );

  /* 
    User has been authenticated via keycloak but fail to update eula, create user org and send invitation
    We need to clear the token and reset the authentication status to clean up.
  */
  const signUpReset = useCallback(() => {
    keycloak?.clearToken();
    setIsAuthenticated(false);
    setIsEulaAccepted(false);
    setUser(undefined);
    localStorage.clear();
  }, [keycloak, setIsAuthenticated, setUser]);

  useEffect(() => {
    const addUserToOrganization = async (
      organizationUuid: string,
      invitationId: string
    ) => {
      if (!user?.identity_id) return;

      setErrorMsg('');

      if (isSignUpRequired) {
        /* 
          For invited user, we need to update their eula acceptance then add them to invited organization.
          For self invited user, this step has been done during their sign up. Hence, it will be skipped here.
        */
        const eulaVersion = sessionStorage.getItem('eula-version') || '';
        const country = sessionStorage.getItem('country') || '';
        const state = sessionStorage.getItem('state') || '';
        try {
          await eulaAcceptance({
            status: false,
            identity_id: user.identity_id,
            version: eulaVersion,
            country,
            state,
          });
        } catch (error) {
          if (error instanceof Error) {
            setErrorMsg(error.message);
          }
          signUpReset();
        }
      }

      /* 
        EULA successfully saved, add user to org
      */
      try {
        await addInvitedUser({
          organization_uuid: organizationUuid,
          invitation_id: invitationId,
        });
        navigate('/drive');
      } catch (error) {
        /* 
          only log this error when location is collected
          in some edge case, user might logged in with a different user and have a valid token.
          and the user got invited with different email. When landed on this page, since user already has the token, the page will try to add the user to the organization since it thinks user has completed the previous sign up steps. but at that time, the token sent along with the api doesn't match the user invited with, hence the api will throw an error.
          In this case, we don't want to log the error since it is expected behavior.
        */
        if (isLocationCollected) {
          if (error instanceof Error) {
            setErrorMsg(error.message);
          } else {
            setErrorMsg('Failed to addInvitedUser: Unknown error occurred');
          }
        }
        signUpReset();
      }
    };

    if (isAuthenticated && organization_uuid && invitation_id) {
      addUserToOrganization(organization_uuid, invitation_id);
    }
  }, [
    addInvitedUser,
    eulaAcceptance,
    invitation_id,
    isAuthenticated,
    isLocationCollected,
    isSignUpRequired,
    navigate,
    organization_uuid,
    signUpReset,
    user?.identity_id,
  ]);

  const updateUserLocation = (country: string, state: string) => {
    sessionStorage.setItem('country', country);
    sessionStorage.setItem('state', state);
    setIsLocationCollected(true);
  };

  const getStepContent = () => {
    if (isNil(organization_uuid) || isNil(invitation_id)) {
      return {
        step: 1,
        content: <InvalidInvitationLink />,
      };
    }
    if (!isLocationCollected) {
      return {
        step: 1,
        content: <UserLocation updateUserLocation={updateUserLocation} />,
      };
    }
    if (isEulaVisible) {
      return {
        step: 3,
        content: (
          <EulaViewer
            setIsEulaVisible={setIsEulaVisible}
            setIsEulaAccepted={setIsEulaAccepted}
          />
        ),
      };
    }
    return {
      step: 2,
      content: (
        <div className={styles['auth-content']}>
          {/* text copy on left */}
          <div className={styles['auth-copy-container']}>
            <h2 className={styles['auth-page-title']}>
              One more step! Sign in to join the organization
            </h2>
            <p className={styles['auth-page-copy']}>Welcome to Ouster Studio</p>
          </div>

          {/* auth form on right */}
          <div className={styles['auth-form-container']}>
            <AuthForm
              isSignUpRequired={isSignUpRequired}
              isPasswordRequirementVisible={isSignUpRequired}
              isEulaAccepted={isEulaAccepted}
              setIsEulaAccepted={setIsEulaAccepted}
              setIsEulaVisible={setIsEulaVisible}
              authMode={AuthMode.REGISTER}
              setPendingAuthMethod={setPendingAuthMethod}
              setPendingFormData={setPendingFormData}
              pendingFormData={pendingFormData}
              pendingAuthMethod={pendingAuthMethod}
              invitationId={invitation_id}
              orgUuid={organization_uuid}
              authorizeError={errorMsg}
            />
          </div>
        </div>
      ),
    };
  };

  const { step: curStep, content } = getStepContent();

  if (!keycloak)
    return (
      <div className={styles['loading-container']}>
        <CircularProgress isIndeterminate size="50px" thickness={10} />
      </div>
    );

  return (
    <div className={styles['auth-container']}>
      {/* back button */}
      <div className={styles['left-panel']}>{/* <NavBackButton /> */}</div>
      {/* stepper - showing current stage */}
      <Stepper prevStep={0} currentStep={curStep} totalSteps={3} />
      {/* login page content */}
      <section className={styles['auth-section']}>
        <div className={styles['mobile-back-button']}>
          <NavBackButton />
        </div>
        {content}
      </section>
    </div>
  );
}
